import { FC, Fragment, createPortal } from "preact/compat";
import { useCallback, useEffect, useRef } from "preact/hooks";
import { Transition } from "@headlessui/react";
import { useSignal } from "@preact/signals";
import { useApp } from "~/core/store";
import { ContentCoords, DiscoveryItemPopoverProps } from "./interfaces";
import useDiscoveryItem from "./useDiscoveryItem";

const DiscoveryItemPopover: FC<DiscoveryItemPopoverProps> = ({
    tag,
    id,
    ctx,
    open,
    buttonRect,
    maxWidth,
    maxHeight,
    children,
    controlled,
    zIndex = 100,
    position = "fixed",
    offset,
}) => {
    const { api } = useApp();
    const info = api.discovery.getByTag(tag);
    const calculated = useRef(false);
    const coords = useSignal<ContentCoords>({});

    const calculateCoords = useCallback(() => {
        const element = ref.current;

        if (!element || controlled) {
            return;
        }

        const bt = buttonRect.top;
        const bl = buttonRect.left;
        const bw = buttonRect.width;
        const bh = buttonRect.height;
        const newCoords: ContentCoords = {};

        if (offset) {
            newCoords.top = bt + offset.top;
            newCoords.left = bl + offset.left;
        } else {
            const rect = element.getBoundingClientRect();
            const margin = 8;

            if (bt + rect.height + bh + margin >= window.innerHeight) {
                newCoords.top = bt - rect.height - margin;
            } else {
                newCoords.top = bt + bh + margin;
            }

            if (bl + rect.width + bw + margin >= window.innerWidth) {
                newCoords.left = bl - rect.width - margin;
            } else {
                newCoords.left = bl + bw + margin;
            }
        }

        coords.value = newCoords;
        calculated.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlled, coords, buttonRect, offset]);

    const { ref, handleContentClick, checkScrollTop } = useDiscoveryItem({
        tag,
        id,
        ctx,
        open,
        calculateCoords,
    });

    useEffect(() => {
        calculateCoords();

        window.addEventListener("resize", calculateCoords);

        return () => {
            window.removeEventListener("resize", calculateCoords);
        };
    }, [calculateCoords, info.value?.detailed]);

    void coords.value;

    const content = (
        <Transition
            as={Fragment}
            show={open}
            afterLeave={() => {
                calculated.current = false;
            }}
            beforeEnter={() => {
                calculateCoords();
                checkScrollTop();
            }}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-105"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-100"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-105"
        >
            <div
                ref={ref}
                style={{
                    top: calculated.current ? coords.value.top : buttonRect.top,
                    right: calculated.current ? coords.value.right : undefined,
                    bottom: calculated.current
                        ? coords.value.bottom
                        : undefined,
                    left: calculated.current
                        ? coords.value.left
                        : buttonRect.left,
                    opacity: calculated.current || controlled ? 1 : 0,
                    width: controlled ? buttonRect.width : undefined,
                    height: controlled ? buttonRect.height : undefined,
                    maxWidth,
                    maxHeight,
                    position,
                    zIndex,
                }}
                onClick={handleContentClick}
                data-discovery-container={tag}
                className="overflow-overlay rounded-lg border border-gray-300 bg-white p-4 shadow-lg"
            >
                {children}
            </div>
        </Transition>
    );

    if (position === "absolute") {
        return content;
    }

    return createPortal(content, document.body);
};

export default DiscoveryItemPopover;
